import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { getUserTier, useAuthStore } from '../stores/auth.store';

// ----- Types -----

interface PostAuthor {
  id: string;
  displayName: string;
  photoUrl: string | null;
}

interface Reaction {
  type: 'Appreciate' | 'Insightful' | 'Resonates' | 'Encouraging';
  count: number;
  userReacted: boolean;
}

interface Reply {
  id: string;
  author: PostAuthor;
  content: string;
  publishedAt: string;
  edited: boolean;
}

interface Post {
  id: string;
  author: PostAuthor;
  content: string;
  publishedAt: string;
  edited: boolean;
  deleted: boolean;
  reactions: Reaction[];
  replyCount: number;
  replies: Reply[];
  isOwn: boolean;
  canEdit: boolean; // within 30 min
}

// ----- Constants -----
const MAX_CONTENT_LENGTH = 2000;
const REACTION_TYPES: Reaction['type'][] = ['Appreciate', 'Insightful', 'Resonates', 'Encouraging'];

// ----- Main Page -----

/**
 * CommunityPage — Public Feed with colour channels,
 * post composer, post cards, reactions, reply threads, and cursor-based pagination.
 */
export function CommunityPage(): React.JSX.Element {
  const tier = getUserTier(useAuthStore());
  const isReadOnly = tier !== 'early_tester';

  const [activeChannel, setActiveChannel] = useState<string>('commons');
  const [posts, setPosts] = useState<Post[]>([]);
  const [composerContent, setComposerContent] = useState('');
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loading, setLoading] = useState(false);

  const channels = [
    { id: 'red', label: 'Red', colour: '#C75B5B' },
    { id: 'orange', label: 'Orange', colour: '#D4874D' },
    { id: 'yellow', label: 'Yellow', colour: '#C9A84C' },
    { id: 'green', label: 'Green', colour: '#5B9E6F' },
    { id: 'turquoise', label: 'Turquoise', colour: '#4D9E9E' },
    { id: 'blue', label: 'Blue', colour: '#4A7FB5' },
    { id: 'purple', label: 'Purple', colour: '#7B5EA7' },
    { id: 'commons', label: 'The Commons', colour: '#1D1C1D' },
  ];

  const activeChannelInfo = channels.find((c) => c.id === activeChannel);

  // Fetch posts when channel changes
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: any[]; nextCursor: string | null }>(
        `/posts?channel=${activeChannel}&limit=20`
      );
      const mapped: Post[] = (data?.items ?? []).map((p: any) => ({
        id: p.id,
        author: p.author ?? { id: '', displayName: 'Anonymous', photoUrl: null },
        content: p.content ?? '',
        publishedAt: p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : '',
        edited: !!p.editedAt,
        deleted: false,
        reactions: (p.reactions ?? []).map((r: any) => ({
          type: r.type,
          count: r.count,
          userReacted: r.userReacted ?? false,
        })),
        replyCount: p.replyCount ?? 0,
        replies: (p.replies ?? []).map((r: any) => ({
          id: r.id,
          author: r.author ?? { id: '', displayName: 'Anonymous', photoUrl: null },
          content: r.content ?? '',
          publishedAt: r.publishedAt ? new Date(r.publishedAt).toLocaleDateString() : '',
          edited: r.edited ?? false,
        })),
        isOwn: p.isOwn ?? false,
        canEdit: false,
      }));
      setPosts(mapped);
      setHasMorePosts(!!data?.nextCursor);
    } catch {
      // If API fails (e.g. not logged in), show empty
      setPosts([]);
      setHasMorePosts(false);
    }
    setLoading(false);
  }, [activeChannel]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  async function handleSubmitPost(e: React.FormEvent) {
    e.preventDefault();
    if (!composerContent.trim() || composerContent.length > MAX_CONTENT_LENGTH) return;
    try {
      await api.post('/posts', { content: composerContent, channel: activeChannel });
      setComposerContent('');
      fetchPosts(); // Refresh
    } catch {
      // Silently fail for now
    }
  }

  function handleLoadMore() {
    setHasMorePosts(false);
  }

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-2xl font-bold text-text-primary sm:text-3xl">Community</h1>
          <p className="mt-2 text-text-secondary">
            Choose a colour channel or The Commons to share and connect with registered users.
          </p>
        </div>

        {/* Colour Channel Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {channels.map((channel) => (
            <button
              key={channel.id}
              onClick={() => { setActiveChannel(channel.id); setComposerContent(''); }}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                activeChannel === channel.id
                  ? 'text-white shadow-sm'
                  : 'bg-white border border-border text-text-secondary hover:border-gray-400'
              }`}
              style={activeChannel === channel.id ? { backgroundColor: channel.colour } : undefined}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: channel.colour }}
                aria-hidden="true"
              />
              {channel.label}
            </button>
          ))}
        </div>

        {/* Channel description */}
        <div className="mb-4 text-sm text-text-secondary">
          {activeChannel === 'commons' ? (
            <p>A shared space for topics that span multiple perspectives, cross-colour conversations, and community-wide discussions.</p>
          ) : activeChannel === 'red' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Red</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Ready</span>, to collaborate on building a solid foundation including the tools, practices, and resources you need to get your work done.</p>
          ) : activeChannel === 'orange' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Orange</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Eager</span>, to collaborate on rediscovering motivation, joy, and the lightness of rising to work challenges.</p>
          ) : activeChannel === 'yellow' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Yellow</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Steady</span>, to collaborate on making work decisions that move you forward steadily, with confidence.</p>
          ) : activeChannel === 'green' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Green</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Connected</span>, to collaborate on strengthening work relationships, collaboration, and your sense of belonging.</p>
          ) : activeChannel === 'turquoise' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Turquoise</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Yourself</span>, to collaborate on expressing your authentic self and embracing what makes your work unique.</p>
          ) : activeChannel === 'blue' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Blue</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Open</span>, to collaborate on exploring new perspectives, researching ideas, and discovering fresh work angles.</p>
          ) : activeChannel === 'purple' ? (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Purple</span> lens, <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>Be Essential</span>, to collaborate on mentoring others, building a work legacy, and contributing to something greater at work.</p>
          ) : (
            <p>Posts about work through the <span className="font-medium" style={{ color: activeChannelInfo?.colour }}>{activeChannelInfo?.label}</span> lens.</p>
          )}
        </div>

        {/* Read-only banner for members */}
        {isReadOnly && (
          <div className="mb-6 rounded-lg border border-border bg-surface-muted p-4 text-center">
            <p className="text-sm text-text-secondary">
              Early Testers can post to the Community.{' '}
              <Link to="/register" className="font-medium text-byron underline hover:text-byron-accent">
                Join as an Early Tester to participate.
              </Link>
            </p>
          </div>
        )}

        {/* Early Tester note about member read-only access */}
        {!isReadOnly && (
          <p className="mb-4 text-xs text-text-muted">
            Note: Members can view posts in read-only mode.
          </p>
        )}

        {/* Post composer */}
        {!isReadOnly && (
        <form onSubmit={handleSubmitPost} className="mb-6 rounded-lg border border-border bg-white p-4">
          <textarea
            value={composerContent}
            onChange={(e) => setComposerContent(e.target.value)}
            placeholder={activeChannel === 'commons' ? 'Share something with the whole community…' : `Share something from the ${activeChannelInfo?.label} perspective…`}
            rows={3}
            maxLength={MAX_CONTENT_LENGTH}
            className="w-full resize-none rounded border border-border bg-white px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1"
            style={{ '--tw-ring-color': activeChannelInfo?.colour } as React.CSSProperties}
            aria-label="Write a post"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-text-muted">
              {composerContent.length}/{MAX_CONTENT_LENGTH}
            </span>
            <button
              type="submit"
              disabled={!composerContent.trim() || composerContent.length > MAX_CONTENT_LENGTH}
              className="rounded px-4 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: activeChannelInfo?.colour }}
            >
              Post
            </button>
          </div>
        </form>
        )}

        {/* Posts */}
        {posts.length === 0 ? (
          <EmptyState
            message={
              activeChannel === 'commons'
                ? 'No posts yet in The Commons. Start a cross-colour conversation!'
                : `No posts yet in the ${activeChannelInfo?.label ?? ''} channel. Be the first to share!`
            }
          />
        ) : (
          <div className="flex flex-col gap-4">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} isReadOnly={isReadOnly} onDelete={async (id) => {
                try {
                  await api.delete(`/posts/${id}`);
                  fetchPosts();
                } catch {
                  setPosts(prev => prev.filter(p => p.id !== id));
                }
              }} />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMorePosts && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleLoadMore}
              className="rounded border border-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Post Card -----

function PostCard({ post, isReadOnly, onDelete }: { post: Post; isReadOnly: boolean; onDelete?: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);

  if (post.deleted) {
    return (
      <article className="rounded-lg border border-border bg-surface-raised p-4">
        <p className="text-sm italic text-text-muted">[deleted]</p>
      </article>
    );
  }

  function handleDelete() {
    // TODO: call DELETE /posts/:id
  }

  function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    // TODO: call PATCH /posts/:id
    setIsEditing(false);
  }

  function handleReact(type: Reaction['type']) {
    api.post(`/posts/${post.id}/reactions`, { type }).then(() => {
      // Trigger parent refresh
      if (onDelete) { /* hack: we need a refresh callback */ }
      window.location.reload();
    }).catch(() => {});
  }

  function handleReport() {
    if (confirm('Report this post for moderation?')) {
      api.post(`/posts/${post.id}/report`, { reason: 'inappropriate' }).then(() => {
        alert('Post reported. Thank you.');
      }).catch(() => {});
    }
  }

  function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyContent.trim()) return;
    api.post(`/posts/${post.id}/replies`, { content: replyContent }).then(() => {
      setReplyContent('');
      window.location.reload();
    }).catch(() => {});
  }

  return (
    <article className="rounded-lg border border-border bg-surface-raised p-4">
      {/* Author header */}
      <div className="mb-2 flex items-center gap-3">
        {post.author.photoUrl ? (
          <img
            src={post.author.photoUrl}
            alt=""
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted">
            <UserIcon />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm font-medium text-text-primary">{post.author.displayName}</p>
          <p className="text-xs text-text-muted">
            {post.publishedAt}
            {post.edited && <span className="ml-1 italic">(edited)</span>}
          </p>
        </div>
        {/* Post actions */}
        <div className="flex items-center gap-2">
          {post.isOwn && post.canEdit && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="text-xs text-text-muted hover:text-text-secondary"
              aria-label="Edit post"
            >
              Edit
            </button>
          )}
          {post.isOwn && (
            <button
              onClick={handleDelete}
              className="text-xs text-text-muted hover:text-agent-red"
              aria-label="Delete post"
            >
              Delete
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(post.id)}
              className="text-xs font-medium text-red-500 hover:text-red-700"
              aria-label="Admin delete post"
            >
              ✕
            </button>
          )}
          {!post.isOwn && (
            <button
              onClick={handleReport}
              className="text-xs text-text-muted hover:text-agent-red"
              aria-label="Report post"
            >
              Report
            </button>
          )}
        </div>
      </div>

      {/* Content or edit form */}
      {isEditing ? (
        <form onSubmit={handleEdit} className="mb-3">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            maxLength={MAX_CONTENT_LENGTH}
            rows={3}
            className="w-full resize-none rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-text-muted">
              {editContent.length}/{MAX_CONTENT_LENGTH}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="rounded border border-border px-3 py-1 text-xs font-medium text-text-secondary hover:bg-surface-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded bg-gail px-3 py-1 text-xs font-medium text-white hover:bg-gail-accent"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-3">
          <p className={`whitespace-pre-wrap text-sm text-text-primary ${!expanded ? 'line-clamp-3' : ''}`}>
            {post.content}
          </p>
          {!expanded && post.content.length > 150 && (
            <button
              onClick={() => setExpanded(true)}
              className="mt-1 text-xs font-medium text-[#611F69] hover:underline"
            >
              Read more
            </button>
          )}
          {expanded && post.content.length > 150 && (
            <button
              onClick={() => setExpanded(false)}
              className="mt-1 text-xs font-medium text-[#611F69] hover:underline"
            >
              Show less
            </button>
          )}
        </div>
      )}

      {/* Reactions row */}
      {!isReadOnly && (
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {REACTION_TYPES.map((type) => {
          const reaction = post.reactions.find((r) => r.type === type);
          const count = reaction?.count ?? 0;
          const active = reaction?.userReacted ?? false;
          return (
            <button
              key={type}
              onClick={() => handleReact(type)}
              className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition ${
                active
                  ? 'border-gail bg-gail-light text-gail'
                  : 'border-border text-text-muted hover:border-gail hover:text-gail'
              }`}
              aria-label={`${type} (${count})`}
            >
              <ReactionIcon type={type} />
              <span>{count > 0 ? count : ''}</span>
            </button>
          );
        })}
      </div>
      )}

      {/* Reply count toggle */}
      <button
        onClick={() => setShowReplies(!showReplies)}
        className="text-xs font-medium text-text-secondary hover:text-text-primary"
      >
        {showReplies ? 'Hide replies' : post.replyCount > 0 ? `${post.replyCount} ${post.replyCount === 1 ? 'reply' : 'replies'}` : 'Reply'}
      </button>

      {/* Reply thread */}
      {showReplies && (
        <div className="mt-3 border-t border-border pt-3">
          {post.replies.length === 0 ? (
            <p className="mb-2 text-xs text-text-muted">No replies yet.</p>
          ) : (
            <ul className="mb-3 flex flex-col gap-2">
              {post.replies.map((reply) => (
                <li key={reply.id} className="flex gap-2">
                  {reply.author.photoUrl ? (
                    <img
                      src={reply.author.photoUrl}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-muted">
                      <SmallUserIcon />
                    </div>
                  )}
                  <div>
                    <p className="text-xs">
                      <span className="font-medium text-text-primary">
                        {reply.author.displayName}
                      </span>{' '}
                      <span className="text-text-muted">
                        {reply.publishedAt}
                        {reply.edited && ' (edited)'}
                      </span>
                    </p>
                    <p className="text-sm text-text-primary">{reply.content}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Reply composer */}
          {!isReadOnly && (
          <form onSubmit={handleReply} className="flex gap-2">
            <input
              type="text"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a reply…"
              maxLength={MAX_CONTENT_LENGTH}
              className="flex-1 rounded border border-border bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
              aria-label="Write a reply"
            />
            <button
              type="submit"
              disabled={!replyContent.trim()}
              className="rounded bg-gail px-3 py-1.5 text-xs font-medium text-white hover:bg-gail-accent disabled:opacity-50"
            >
              Reply
            </button>
          </form>
          )}
        </div>
      )}
    </article>
  );
}

// ----- Shared Components -----

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
        active
          ? 'bg-surface-raised text-text-primary shadow-sm'
          : 'text-text-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-8 text-center">
      <p className="text-sm text-text-muted">{message}</p>
    </div>
  );
}

// ----- Icons -----

function ReactionIcon({ type }: { type: Reaction['type'] }) {
  switch (type) {
    case 'Appreciate':
      return <span aria-hidden="true">👏</span>;
    case 'Insightful':
      return <span aria-hidden="true">💡</span>;
    case 'Resonates':
      return <span aria-hidden="true">🎯</span>;
    case 'Encouraging':
      return <span aria-hidden="true">💪</span>;
  }
}

function UserIcon() {
  return (
    <svg className="h-4 w-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}

function SmallUserIcon() {
  return (
    <svg className="h-3 w-3 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}
